import React, {useState} from "react";
import Page, {LabeledSection, LabeledValue} from "../../../components/Layout/Page";
import {useTranslation} from "react-i18next";
import EditIcon from "@mui/icons-material/Edit";
import {ColumnDef} from "../../../components/ExTable";
import {
    RoadsoftConfig,
    RoadsoftConfigField,
    RoadsoftHistory,
    RoadsoftHistoryField,
} from "../../../app/api/model/roadsoft";
import SimpleTable from "../../../components/ExTable/SimpleTable";
import {Box, Grid, Typography} from "@mui/material";
import {ReactComponent as EmptyBoxIcon} from "../../../assets/icons/empty_box.svg";
import {Colors} from "../../../components/Layout/Colors";
import {getSubscriptionLabel} from "./helpers";
import RoadsoftSubscriptionDialog from "../components/RoadsoftSubscriptionDialog";
import {camelCase} from "lodash";
import {formatTimestamp} from "../../../utils/datetime";
import {PermissionLevel} from "../../../app/api/model/user";

interface RoadsoftDetailsProps {
    roadsoftConfig?: RoadsoftConfig;
    organizationId: number;
    organizationCountry: string;
}

export default function RoadsoftDetails({roadsoftConfig, organizationId, organizationCountry}: RoadsoftDetailsProps) {
    const {t} = useTranslation();
    const [isUpdateRoadsoftDialogOpen, setIsUpdateRoadsoftDialogOpen] = useState(false);

    const historyColumnsDef: ColumnDef<RoadsoftHistory>[] = [
        {
            fieldName: RoadsoftConfigField.SUBSCRIPTION,
            label: t("generic_action"),
            sortable: false,
            align: "left",
            cellContentNode: ({row}) => getSubscriptionLabel(row[RoadsoftConfigField.SUBSCRIPTION], t),
        },
        {
            fieldName: RoadsoftHistoryField.MODIFIED_AT,
            label: t("generic_modified"),
            sortable: false,
            align: "left",
            formatCellText: ({row}) => formatTimestamp(row[RoadsoftHistoryField.MODIFIED_AT]),
        },
        {
            fieldName: RoadsoftHistoryField.USER,
            label: t("generic_user"),
            sortable: false,
            align: "left",
            formatCellText: ({row}) => row[RoadsoftHistoryField.USER] || t("generic_unknown"),
        },
    ];

    return (
        <Page>
            <LabeledSection
                title={t("generic_roadsoft")}
                xs={12}
                buttons={[
                    {
                        key: "update",
                        tooltip: "action_update",
                        disabled: isUpdateRoadsoftDialogOpen,
                        minRequiredPermissionToSee: PermissionLevel.MANAGER,
                        callback: () => setIsUpdateRoadsoftDialogOpen(true),
                        icon: <EditIcon />,
                    },
                ]}
            >
                <LabeledValue
                    label={t("generic_roadsoftSubscription")}
                    content={t(`roadsoft_subscription${camelCase(roadsoftConfig?.[RoadsoftConfigField.SUBSCRIPTION])}`)}
                />
            </LabeledSection>
            <LabeledSection title={t("generic_history")} xs={12}>
                {roadsoftConfig?.[RoadsoftConfigField.HISTORY]?.length ? (
                    <SimpleTable columnDefs={historyColumnsDef} entries={roadsoftConfig[RoadsoftConfigField.HISTORY]} />
                ) : (
                    <Grid container justifyContent="center" alignItems="center" style={{height: "100px"}}>
                        <Box sx={{display: "flex", flexDirection: "column", alignItems: "center"}}>
                            <EmptyBoxIcon fill={Colors.GRAY_115} />
                            <Typography>{t("generic_noData")}</Typography>
                        </Box>
                    </Grid>
                )}
            </LabeledSection>
            <RoadsoftSubscriptionDialog
                open={isUpdateRoadsoftDialogOpen}
                onClose={() => setIsUpdateRoadsoftDialogOpen(false)}
                organizationId={organizationId}
                organizationCountry={organizationCountry}
                currentSubscription={roadsoftConfig?.[RoadsoftConfigField.SUBSCRIPTION]}
            />
        </Page>
    );
}
